import { useState, useRef } from 'react';
import styled from 'styled-components/macro';
import classNames from 'classnames';
import { ReactComponent as Pin } from '../../assets/pin.svg';
import { ProductType } from '../../config/products';
import useTranslations from '../utils/language-provider';
import { usePopperTooltip } from 'react-popper-tooltip';
import NewFlag from './newflag';
import arrowRight from '../../assets/arrow-right.svg';
import { KeepScale } from 'react-zoom-pan-pinch';

type ProductPinProps = {
    productType: ProductType;
    showNewFlag?: boolean;
    style?: React.CSSProperties;
    className?: string;
    popoverOpenDefault?: boolean;
    customMenuContent?: React.ReactNode;
    isAlternativeProducts?: boolean;
    isSideBar?: boolean;
    coordinates?: { x: number; y: number };
    pinCoordinates?: { x: number; y: number };
};

const PinInner = styled.div<{ coordinates: { x: number; y: number } | undefined }>`
    position: relative;
    z-index: ${(props) => props.theme.zIndex.content};
    transform-origin: center;
    height: 30px;
    width: 30px;

    @keyframes pulse {
        0% {
            transform: scale(1);
        }
        100% {
            transform: scale(1.2);
        }
    }

    &:hover {
        animation-play-state: paused;
    }

    .pin {
        animation: pulse 2.2s alternate ease-in-out infinite;
        position: absolute;
        ${(props) => (props.coordinates !== undefined ? `top: ${props.coordinates.y/3}px; left:${props.coordinates.x/3}px` : ``)};
    }

    @media ${(props) => props.theme.screens.lg} {
        height: 30px;
        width: 30px;

        .pin {
            ${(props) => (props.coordinates !== undefined ? `top: ${props.coordinates.y}px; left:${props.coordinates.x}px` : ``)};
        }
    }
`;

const PinWrapper = styled(KeepScale)`
    position: relative;
    z-index: ${props => props.theme.zIndex.content};
    color: #fff;
    margin-top: -20px;
    margin-left: -20px;

    @media ${props => props.theme.screens.lg} {
        margin-top: -15px;
        margin-left: -15px;
    }

    &.front {
        z-index: ${props => props.theme.zIndex.popover};
    }

    ${PinInner}:hover {
        cursor: pointer;

        .pin {
            color: ${props => props.theme.colors.primaryLight};
        }

        .arrow {
            top: 12px;
        }
    }
`

const MenuItem = styled.li`
    white-space: nowrap;
`

const MenuItemHead = styled(MenuItem)`
    padding: 4px 5px 3px;
    font-weight: bold;
    color: #fff;
    font-size: ${props => props.theme.typography.md};

    @media ${props => props.theme.screens.lg} {
        padding: 9px 23px 7px;
        font-size: ${props => props.theme.typography.xl};
    }
`

const MenuLink = styled.a`
    display: block;
    padding: 4px 5px 3px;
    font-size: ${props => props.theme.typography.sm};
    cursor: pointer;
    color: ${props => props.theme.colors.white};
    text-decoration: none;
    text-align: left;

    @media ${props => props.theme.screens.lg} {
        padding: 2px 23px;
        font-size: ${props => props.theme.typography.md};
    }

    &:hover {
        color: ${props => props.theme.colors.white};
        background: linear-gradient(90deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryLightContrast} 100%);
    }
`

const Menu = styled.ul`
    opacity: 0;
    list-style: none;
    margin: 0;
    padding: 0px 10px 10px;
    background: ${props => props.theme.colors.primary80};
    transition: opacity 200ms;

    &.open{
        opacity: 1;
    }
`;

const Popper = styled.div<{ flagPlacement: number }>`
    max-width: none !important;
    margin-bottom: 10px;
    margin-left: 20px;
    ${MenuItem},
    ${MenuItemHead} {
        position: relative;
        opacity: 0;
        transition: top 200ms, opacity 200ms;
    }

    > :last-child:not(${Menu}) {
        opacity: 0;
        transition: opacity 200ms;
    }

    &.open {
        &::after {
            opacity: 1;
        }
        ${MenuItem},
        ${MenuItemHead} {
            opacity: 1;
            top: 0;
        }
        > :last-child:not(${Menu}) {
            opacity: 1;
        }
        ${MenuLink}{
        display: flex;
        justify-content: space-between;
        align-items: center;
        &::after{
            content: url(${arrowRight});
            color: ${(props) => props.theme.colors.white};
            display: inline-block;
            width: 15px;
            aspect-ratio: 1 / 1;
            opacity: 0.5;
            margin-left: 15px;
        }
    }
}

    &::after {
        content: "";
        opacity: 0;
        position: absolute;
        transition: all 100ms ease-in;
        z-index: ${(props) => props.theme.zIndex.base};
        bottom: -15px;
        left: 0px;
        margin-left: 0;
        border-left: 15px solid ${(props) => props.theme.colors.primary80};
        border-bottom: 15px solid transparent;

        @media ${(props) => props.theme.screens.lg} {
            bottom: -28px;
            border-left-width: 29px;
            border-bottom-width: 29px;
        }
    }

    &[data-popper-placement*="top-end"]{
        margin-right: 20px;
        margin-left: 0;
    }

    &[data-popper-placement*="top-end"]::after {
        left: auto;
        right: 0px;
        border-left: none;
        border-right: 15px solid ${(props) => props.theme.colors.primary80};

        @media ${(props) => props.theme.screens.lg} {
            bottom: -28px;
            border-right-width: 29px;
        }
    }

    &[data-popper-placement*="bottom-start"]{
        margin-bottom: 0;
        margin-top: 10px;
    }

    &[data-popper-placement*="bottom-start"]::after {
        bottom: 92px;
        border-bottom: none;
        border-top: 15px transparent solid;

        @media ${(props) => props.theme.screens.lg} {
            bottom: 116px;
            border-top-width: 29px;
        }
    }

    &[data-popper-placement*="right"]{
        margin-left: 12px;
    }
    &[data-popper-placement*="right"]::after{
        margin-bottom: calc(33% + ${props => props.flagPlacement/3}px);
        margin-left: -15px;
        transform: rotate(180deg);
    }

    &[data-popper-placement*="bottom-end"]{
        margin-top: 10px;
        margin-right: 15px;
        margin-left: 0;
    }

    &[data-popper-placement*="bottom-end"]::after {
        bottom: 92px;
        right: 0;
        border-bottom: none;
        border-top: 15px transparent solid;
        border-right: 15px solid #0e233dcc;
        border-left: none;

        @media ${(props) => props.theme.screens.lg} {
            bottom: 116px;
            border-top-width: 29px;
        }
    }

    @media ${(props) => props.theme.screens.lg} {
        margin-bottom: 30px;
        margin-left: 25px;

        &[data-popper-placement*="right"]::after{
            margin-bottom: calc(33% + ${props => props.flagPlacement}px);
            margin-left: -29px;
        }
        &[data-popper-placement*="bottom-start"]{
            margin-bottom: 0;
            margin-top: 24px;
        }
    }
`;

const ProductPin = ({
    style = {},
    className = '',
    showNewFlag = false,
    popoverOpenDefault = false,
    productType,
    customMenuContent,
    isAlternativeProducts,
    isSideBar = false,
    coordinates = {x: 0, y:0},
    pinCoordinates
}: ProductPinProps) => {
    const [isOpen, setIsOpen] = useState(popoverOpenDefault);
    const [isPinned, setIsPinned] = useState(popoverOpenDefault);
    const [menuOpen, setMenuOpen] = useState(popoverOpenDefault);
    const closeTimeoutRef = useRef<NodeJS.Timeout>();
    const openTimeoutRef = useRef<NodeJS.Timeout>();

    const translations = useTranslations();
    const product = translations.products[productType];

    const { getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
        placement: isSideBar ? 'right' : coordinates.y > 450 ? 'top-start' : 'bottom-start',
        trigger: 'click',
        visible: isOpen,
        onVisibleChange(state) {
            if (state === false) {
                setMenuOpen(false);
            }
        },
    });

    const clearTimeouts = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = undefined;
        }
    };

    const openMenu = () => {
        clearTimeouts();
        setIsOpen(true);
        window.dispatchEvent(new CustomEvent('debug-product-popover', {detail: {productType}}));

        openTimeoutRef.current = setTimeout(() => {
            setMenuOpen(true);
        }, 100);
    };

    const closeMenu = () => {
        if (isPinned) return;

        clearTimeouts();
        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            setMenuOpen(false);
            window.dispatchEvent(new CustomEvent('debug-product-popover', {detail: {productType: undefined}}));
        }, 300);
    };

    const handlePinMouseEnter = () => {
        openMenu();
    };

    const handlePinClick = () => {
        setIsPinned(!isPinned);
        if (!isPinned) {
            openMenu();
        }
    };

    const handlePopperMouseEnter = () => {clearTimeouts();};

    if (!product) {
        return null;
    }
    return (
        <PinWrapper style={style} className={classNames(className, { front: menuOpen })}>
            <PinInner
                ref={setTriggerRef}
                onMouseEnter={handlePinMouseEnter}
                onMouseLeave={() => closeMenu()}
                onClick={handlePinClick}
                coordinates={
                    isAlternativeProducts
                        ? {
                            x: (pinCoordinates?.x ?? coordinates.x) - coordinates.x,
                            y: (pinCoordinates?.y ?? coordinates.y) - coordinates.y
                        }
                        : undefined
                }
            >
                <Pin className='pin' />
            </PinInner>

            {showNewFlag && <NewFlag type='product'>{translations.buildingDetails.new}</NewFlag>}

            {visible && (
                <Popper
                    ref={setTooltipRef}
                    {...getTooltipProps()}
                    className={classNames({ open: menuOpen })}
                    onMouseEnter={handlePopperMouseEnter}
                    onMouseLeave={() => closeMenu()}
                    flagPlacement={coordinates.y - (pinCoordinates?.y ?? coordinates.y)}
                >
                    <Menu className={classNames({ open: menuOpen })}>
                        <MenuItemHead style={{ transitionDelay: `${product.links.length * 100 + 100}ms` }}>
                            {product.name}
                        </MenuItemHead>

                        {isAlternativeProducts ? (
                            <MenuItem
                                key={product.links[0].text}
                                style={{ transitionDelay: `${product.links.length * 100}ms` }}
                            >
                                <MenuLink href={product.links[0].href}>{product.links[0].text}</MenuLink>
                                <MenuLink href={'https://www.ksb.com/de-de/loesungen/data-center'}>
                                    Zur Detailseite Rechenzentrum
                                </MenuLink>
                            </MenuItem>
                        ) : (
                            product.links.map((link, index) => (
                                <MenuItem
                                    key={link.text + index}
                                    style={{ transitionDelay: `${(product.links.length - index) * 100}ms` }}
                                >
                                    <MenuLink href={link.href}>{link.text}</MenuLink>
                                </MenuItem>
                            ))
                        )}
                    </Menu>
                    {customMenuContent}
                </Popper>
            )}
        </PinWrapper>
    );
}

export default ProductPin;
